<template>
    <InvoicingDisplaySettingsLoader v-if="loading" />
    <CompassTable
        v-else
        :isCheckable="false"
        class="invoicing-display-fields-table"
        :size="CompassEnums.TableSize.MEDIUM"
    >
        <CompassTableHead>
            <CompassTableHeadCell>
                {{ headerLabel }}
            </CompassTableHeadCell>
            <CompassTableHeadCell>
                {{ displayedOnDocumentLabel }}
            </CompassTableHeadCell>
        </CompassTableHead>
        <CompassTableRow v-for="item in fields" :key="item.field">
            <CompassTableBodyCell class="typographies-paragraph-medium-regular">
                {{ item.label }}
            </CompassTableBodyCell>
            <CompassTableBodyCell class="invoicing-display-fields-table__fixed-width-cell">
                <CompassCheckbox
                    v-model="item.field"
                    :checked="item.value"
                    :showLabel="false"
                    @change="(newValue: boolean) => emit('change', newValue, item.field)"
                />
                <CompassTooltip v-if="item.isForced" :content="forcedFieldTooltip" :arrow="false">
                    <CompassPill
                        :label="$t.__('settings.invoicingDisplay.isForced')"
                        icon="eyeshow-fill"
                        :variant="CompassEnums.PillVariant.PASTEL"
                        :color="CompassEnums.PillColor.ORANGE"
                        :size="CompassEnums.PillSize.EXTRA_SMALL"
                    />
                </CompassTooltip>
            </CompassTableBodyCell>
        </CompassTableRow>
    </CompassTable>
</template>

<script setup lang="ts">
import { inject } from 'vue';
import { Translator } from 'js/shims-vue';
import InvoicingDisplaySettingsLoader from 'vcomponents/settings/invoicing/InvoicingDisplaySettingsLoader.vue';
import { type DisplayPreferences } from 'api/settings/invoicing/invoicingDisplaySettingsService';

export interface FieldEntry {
    field: keyof DisplayPreferences;
    label: string;
    value: boolean;
    isForced: boolean;
}

interface Props {
    headerLabel: string;
    fields: FieldEntry[];
    loading: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
    change: [newValue: boolean, field: keyof DisplayPreferences];
}>();

const $t = inject('$t') as Translator;

const displayedOnDocumentLabel = $t.__('settings.invoicingDisplay.table.displayedOnDocument');
const forcedFieldTooltip = $t.__('settings.invoicingDisplay.isForced.tooltip');
</script>

<style lang="scss" scoped>
.invoicing-display-fields-table {
    :deep(.compass-pill) {
        margin: 0 0 0 var(--spacing-3);
    }

    &__fixed-width-cell {
        white-space: nowrap;
        width: 1%;
    }
}
</style>
